import React, { FC, ReactNode } from "react";
import { HsBoltIcon, HsCloseIcon, HsEditIcon, HsInfoIcon, HsRefreshIcon, HsSlidersIcon, HsStarIcon } from "./icons";
import "./hubspot-pages.css";

/**
 * Page-level parts of HubSpot's UI, for the screens built as HubSpot pages:
 * the reports dashboard (rpt-*.json) and the Users & Teams list
 * (settings-users-page.json, settings-view-tabs-with-counts.json). Each part
 * names the capture it is built from; hubspot-pages.css holds the values.
 */

const cx = (...names: (string | false | null | undefined)[]): string => names.filter(Boolean).join(" ");

/* ── badges and tags ── */

/**
 * HubSpot's badge: a 20px pill, 10/18 at 600, 7px sides. "grey" is a report
 * card's date range chip (rpt-report-card.json), "dark" a view tab's count,
 * "lavender" the Super Admin badge (settings-users-page.json).
 */
export const HsBadge: FC<{ tone?: "grey" | "dark" | "lavender"; children: ReactNode; className?: string }> = ({ tone = "grey", children, className }) => (
  <span className={cx("hs-badge", `hs-badge--${tone}`, className)}>{children}</span>
);

/* ── the reports dashboard (rpt-dashboard-header.json, rpt-dashboard-filterbar.json) ── */

/** The dashboard title: the star, then the 24px title as a dropdown with its 15px caret. */
export const HsDashboardTitle: FC<{ title: ReactNode; starred?: boolean; onStar?: () => void; onOpen: (anchor: HTMLElement) => void; open?: boolean }> = ({
  title,
  starred,
  onStar,
  onOpen,
  open,
}) => (
  <h1 className="hs-dash-title">
    <button type="button" className="hs-dash-title__star hs-focusable" aria-pressed={!!starred} aria-label={starred ? "Remove from favorites" : "Add to favorites"} onClick={onStar}>
      <HsStarIcon />
    </button>
    <button type="button" className="hs-dash-title__button hs-focusable" aria-haspopup="true" aria-expanded={!!open} data-hs-dashboard-switch="" onClick={(e) => onOpen(e.currentTarget)}>
      <span className="hs-dash-title__label">{title}</span>
      <span className="hs-dash-title__caret" aria-hidden="true" />
    </button>
  </h1>
);

/** The header row: title on the left, the actions on the right, a #CCC rule under it. */
export const HsDashboardHeader: FC<{ title: ReactNode; actions?: ReactNode }> = ({ title, actions }) => (
  <header className="hs-dash-header">
    {title}
    {actions && <div className="hs-dash-header__actions">{actions}</div>}
  </header>
);

/** The filter row: "Quick filters", the pinned filters, a divider, "Advanced filters", and refresh on the far right. */
export const HsDashboardFilterBar: FC<{ quickFilters: ReactNode; advanced?: ReactNode; onRefresh?: () => void; refreshing?: boolean }> = ({
  quickFilters,
  advanced,
  onRefresh,
  refreshing,
}) => (
  <div className="hs-dash-filters">
    <div className="hs-dash-filters__left">
      <span className="hs-dash-filters__label">
        <HsEditIcon />
        Quick filters
      </span>
      <ul className="hs-dash-filters__list">{quickFilters}</ul>
      {advanced && (
        <>
          <span className="hs-dash-filters__divider" aria-hidden="true" />
          {advanced}
        </>
      )}
    </div>
    {onRefresh && (
      <button type="button" className="hs-dash-filters__refresh hs-focusable" aria-label="Refresh" title="Refresh" onClick={onRefresh} data-refreshing={refreshing ? "true" : undefined}>
        <HsRefreshIcon />
      </button>
    )}
  </div>
);

/** A pinned quick filter: the lightning bolt, its name, the 12px chevron; teal once it filters. */
export const HsQuickFilter: FC<{ label: ReactNode; active?: boolean; open?: boolean; onOpen: (anchor: HTMLElement) => void } & Record<`data-${string}`, string>> = ({
  label,
  active,
  open,
  onOpen,
  ...rest
}) => (
  <li>
    <button
      type="button"
      className="hs-quick-filter hs-focusable"
      data-active={active ? "true" : undefined}
      aria-haspopup="dialog"
      aria-expanded={!!open}
      onClick={(e) => onOpen(e.currentTarget)}
      {...rest}
    >
      <HsBoltIcon className="hs-quick-filter__bolt" />
      <span>{label}</span>
      <svg className="hs-quick-filter__chevron" viewBox="0 0 32 32" aria-hidden="true">
        <path d="M5 11.5l11 9 11-9" />
      </svg>
    </button>
  </li>
);

/** "Advanced filters": the sliders icon and the 14/18 label, a 40px transparent pill. */
export const HsAdvancedFiltersButton: FC<{ onClick: (anchor: HTMLElement) => void; count?: number; expanded?: boolean }> = ({ onClick, count, expanded }) => (
  <button type="button" className="hs-advanced-filters hs-focusable" aria-haspopup="dialog" aria-expanded={!!expanded} onClick={(e) => onClick(e.currentTarget)}>
    <HsSlidersIcon />
    <span>Advanced filters{count ? ` (${count})` : ""}</span>
  </button>
);

/** The filter popover's head (rpt-daterange-open.json): bolt + 16/600 title, the remove and Apply pills, the field's type underneath. */
export const HsFilterPopoverHead: FC<{ title: ReactNode; type?: ReactNode; typeIcon?: ReactNode; onApply?: () => void; applyDisabled?: boolean; onRemove?: () => void }> = ({
  title,
  type,
  typeIcon,
  onApply,
  applyDisabled,
  onRemove,
}) => (
  <div className="hs-filter-pop__head">
    <div className="hs-filter-pop__title-row">
      <h5 className="hs-filter-pop__title">
        <HsBoltIcon />
        {title}
      </h5>
      <span className="hs-filter-pop__buttons">
        {onRemove && (
          <button type="button" className="hs-btn hs-btn--secondary hs-btn--icon hs-btn--xs hs-focusable" aria-label="Remove" onClick={onRemove}>
            <HsCloseIcon />
          </button>
        )}
        {onApply && (
          <button type="button" className="hs-btn hs-btn--primary hs-filter-pop__apply hs-focusable" disabled={applyDisabled} onClick={onApply}>
            Apply
          </button>
        )}
      </span>
    </div>
    {type && (
      <p className="hs-filter-pop__type">
        {typeIcon}
        {type}
      </p>
    )}
  </div>
);

/** The dashboard's grid of report cards: 10px gaps, 191px rows. */
export const HsReportGrid: FC<{ children: ReactNode }> = ({ children }) => <section className="hs-report-grid">{children}</section>;

/**
 * A report card (rpt-report-card.json): the #CCC 16px-cornered tile, a teal
 * 16/600 title with its info icon, the grey date chips, then the report.
 * `span` columns and `rows` grid rows.
 */
export const HsReportCard: FC<{
  title: ReactNode;
  info?: ReactNode;
  chips?: ReactNode[];
  span?: 1 | 2 | 3;
  rows?: 1 | 2 | 3;
  onTitleClick?: () => void;
  children: ReactNode;
  className?: string;
}> = ({ title, info, chips, span = 1, rows = 1, onTitleClick, children, className }) => (
  <div className={cx("hs-report-card", `hs-report-card--span-${span}`, `hs-report-card--rows-${rows}`, className)} data-hs-card={typeof title === "string" ? title : undefined}>
    <div className="hs-report-card__tile">
      <div className="hs-report-card__header">
        <h5 className="hs-report-card__title">
          {onTitleClick ? (
            <button type="button" className="hs-report-card__link hs-focusable" onClick={onTitleClick}>
              {title}
            </button>
          ) : (
            <span className="hs-report-card__link">{title}</span>
          )}
          {info && (
            <span className="hs-report-card__info" title={typeof info === "string" ? info : undefined} aria-label={typeof info === "string" ? info : undefined}>
              <HsInfoIcon />
            </span>
          )}
        </h5>
      </div>
      {chips && chips.length > 0 && (
        <div className="hs-report-card__chips">
          {chips.map((c, i) => (
            <HsBadge key={i}>{c}</HsBadge>
          ))}
        </div>
      )}
      <div className="hs-report-card__body">{children}</div>
    </div>
  </div>
);

/** The KPI "data well" (rpt-kpi-card.json): centred 12/18 uppercase labels over 28/36 teal numbers. */
export const HsDataWell: FC<{
  items: { label: ReactNode; value: ReactNode; onClick?: () => void; note?: ReactNode; change?: { text: string; direction: "up" | "down" | "flat"; good?: boolean } | null }[];
}> = ({ items }) => (
  <ul className="hs-datawell" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
    {items.map((it, i) => (
      <li key={i} className="hs-datawell__item">
        <p className="hs-datawell__label">{it.label}</p>
        <span className="hs-datawell__value">
          {it.onClick ? (
            <button type="button" className="hs-datawell__number hs-focusable" onClick={it.onClick}>
              {it.value}
            </button>
          ) : (
            <span className="hs-datawell__number">{it.value}</span>
          )}
        </span>
        {it.note && <span className="hs-datawell__change">{it.note}</span>}
        {it.change && (
          <span className="hs-datawell__change" data-direction={it.change.direction} data-good={it.change.good === undefined ? undefined : String(it.change.good)}>
            {it.change.direction === "up" ? "▲" : it.change.direction === "down" ? "▼" : "–"} {it.change.text}
          </span>
        )}
      </li>
    ))}
  </ul>
);

/* ── list pages (settings-users-page.json) ── */

/**
 * View tabs with counts (settings-view-tabs-with-counts.json): 40px tabs in a
 * #CCC frame, the current one white and open into the list below, each with
 * its count in a dark badge. `trailing` sits at the right end of the row.
 */
export const HsCountTabs: FC<{ options: { value: string; label: ReactNode; count?: number }[]; value: string; onChange: (value: string) => void; trailing?: ReactNode }> = ({
  options,
  value,
  onChange,
  trailing,
}) => (
  <div className="hs-count-tabs">
    <div className="hs-count-tabs__list" role="tablist">
      {options.map((o) => (
        <button key={o.value} type="button" role="tab" aria-selected={o.value === value} className="hs-count-tab hs-focusable" onClick={() => onChange(o.value)}>
          {o.count !== undefined && <HsBadge tone="dark">{o.count}</HsBadge>}
          <span className="hs-count-tab__label">{o.label}</span>
        </button>
      ))}
    </div>
    {trailing && <div className="hs-count-tabs__trailing">{trailing}</div>}
  </div>
);

/** The list's filter bar: quick filters on the left, view buttons on the right, framed left and right. */
export const HsListFilterBar: FC<{ children: ReactNode; trailing?: ReactNode }> = ({ children, trailing }) => (
  <div className="hs-list-filters" role="group" aria-label="Filters">
    <div className="hs-list-filters__left">{children}</div>
    {trailing && <div className="hs-list-filters__right">{trailing}</div>}
  </div>
);

/** A list quick filter: "Status ▾", grey and with an × once it filters ("Status (1)"). */
export const HsListQuickFilter: FC<{ label: ReactNode; count?: number; onOpen: (anchor: HTMLElement) => void; onClear?: () => void }> = ({ label, count, onOpen, onClear }) => (
  <div className="hs-list-filter" data-active={count ? "true" : undefined}>
    <button type="button" className="hs-list-filter__button hs-focusable" aria-haspopup="dialog" onClick={(e) => onOpen(e.currentTarget)}>
      <span>
        {label}
        {count ? ` (${count})` : ""}
      </span>
      <span className="hs-caret hs-caret--md" aria-hidden="true" />
    </button>
    {count && onClear ? (
      <button type="button" className="hs-list-filter__clear hs-focusable" aria-label="Clear filter" onClick={onClear}>
        <HsCloseIcon />
      </button>
    ) : null}
  </div>
);

/** The bar over the table: search on the left, small pills on the right. */
export const HsTableTopBar: FC<{ left: ReactNode; right?: ReactNode }> = ({ left, right }) => (
  <div className="hs-table-topbar">
    <div className="hs-table-topbar__left">{left}</div>
    {right && <div className="hs-table-topbar__right">{right}</div>}
  </div>
);
